/*
 * Ryan O'Dowd
 * 2019-06-08
 * © Copyright 2019 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import {
  Image,
  View,
} from 'react-native';
import {
  READ_ROUTE,
  addToReadingHistory,
  // @TODO: fetchPassageOfTheDay,
  fetchWebStatsMsSpent,
  fetchWebStatsNumVersesReviewed,
  // @TODO: setUser,
} from '@utils/store/actions';
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import Button from '@components/common/Button';
import CPassage from '@classes/Passage';
import ContactForm from '@website/components/ContactForm';
import Dialog from '@components/common/Dialog';
import DownloadLinks from '@website/components/DownloadLinks';
import IconButton from '@components/common/IconButton';
import PassageDisplay from '@components/PassageDisplay';
import ShareButton from '@components/ShareButton';
import Text from '@components/common/Text';
import WebPageStub from '@website/containers/WebPageStub';
import WebsiteSection from '@website/components/WebsiteSection';
import fb from '@utils/firebase';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const LandingPage = () => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);
  const locationState = hooks.useLocationState();
  const navigation = hooks.useNavigation();

  const msSpent = hooks.useTypedSelector((state) => state.webStatsMsSpent);
  const versesReviewed = hooks.useTypedSelector((state) => state.webStatsNumVersesReviewed);

  const [_passage, setPassage]: any = useState({}); // @TODO: {} is an odd default, but better than null checking below
  const hasFetchedPassage = useRef(false);
  const [_passageText, setPassageText] = useState('');

  hooks.useFetchInitialData(1, true); // @TODO: don't hardcode locale_id

  // @TODO: const showBeta = new URLSearchParams(window.location.search).get('beta');

  //const preferredTranslation = hooks.usePreferredTranslation();
  //const passageOfTheDay = hooks.useTypedSelector((state: any) => state.passageOfTheDay.passage || {}, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue)); // @TODO: shouldn't need the || {} but be careful of missing from back end or initial state

  //const passage = new CPassage(
  //  -1, // @TODO: was using passageOfTheDay.id before, but that means that the back end is saving the lessons with the passage_id of passageOfTheDay.id instead of the passage.id; this is hacky to use -1, but it's required right now because of the _saveLesson function in Practice/index.native.js
  //  preferredTranslation.id || 3, // @TODO: race condition here on initial login on web (white screen issue)
  //  passageOfTheDay.book_id,
  //  passageOfTheDay.chapter_number,
  //  passageOfTheDay.start_verse_number,
  //  passageOfTheDay.end_verse_number,
  //);

  const passageParts = hooks.usePassageApiNotationToParts(locationState?.p);
  useEffect(() => {
    let isMounted = true;
    const fetchPassage = async () => {
      try {
        const result = await utilities.createNewPassage( // @TODO: this should contain range start/end if we want this to work fully...but right now there are only issues if you munge the url...can't get into that case using the ui
          passageParts.translationId,
          passageParts.bookId,
          passageParts.chapterNumber,
          passageParts.startVerseNumber,
          passageParts.endVerseNumber,
        );

        if (isMounted) {
          setPassage(CPassage.createFromObject(result));
        }
      } catch (e) {
        fb.recordError(fb.crashlytics, e);
        if (isMounted) {
          setPassage({});
        }
      }

      hasFetchedPassage.current = true;
    };

    if (passageParts && !hasFetchedPassage.current) {
      // only need to fetch passage one time
      fetchPassage();
    }

    return () => {
      isMounted = false;
    };
  }, [passageParts]);

  useEffect(() => { // @TODO: deprecated in 7.3.7
    if (!Object.keys(_passage).length && locationState?.passage) {
      setPassage(locationState.passage);
    }
  }, [setPassage, _passage, locationState?.passage]);

  const isPassageDialogOpen = !!Object.keys(_passage || {}).length;

  useEffect(() => { // @TODO: usnsubscribe? should we return from this useEffect
    dispatch(fetchWebStatsMsSpent()); // @TODO: this shouldn't be a live stat...maybe update every month or so, but i don't want people calling this api on every page load
    dispatch(fetchWebStatsNumVersesReviewed()); // @TODO: this shouldn't be a live stat...maybe update every month or so, but i don't want people calling this api on every page load
    // @TODO: dispatch(fetchPassageOfTheDay());
    fb.getRedirectResult(fb.auth).then((result: any) => { // @TODO: is this best? don't like that only web uses getRedirectResult
      if (result?.user) {
        fb.logEvent(fb.analytics, 'login', { // @TODO: abstract with other calls to robots
          displayName: result.user.displayName,
          authType: result.credential.providerId.replace('.com', ''), // google.com, facebook.com
        });
        // @TODO: firestore.setUser(result.user.uid);
        // @TODO: dispatch(setUser(result.user));
      }
    }).catch((error) => {
      fb.recordError(fb.crashlytics, error);
    });
  }, [dispatch]);

  return (
    <WebPageStub>
      <WebsiteSection
        isDarkBackground={true}
        title='Memorize Bible verses more easily'
      >
        <Text style={[styles.paragraph, styles.subTitle]}>{"Interact with God's Word in a new way."}</Text>
        <DownloadLinks />
      </WebsiteSection>

      <WebsiteSection
        title='How it works'
      >
        <Text style={styles.paragraph}>{i18n('wrinklyAims')}</Text>
        <Text style={styles.paragraph}>{i18n('readPlayGamesAndTest')}</Text>
        <View style={styles.imagesWrapper}>
          <Image style={styles.image} width={205} height={444} source={require('@assets/img/store_presence/1.png')} alt='track' />
          <Image style={styles.image} width={205} height={444} source={require('@assets/img/store_presence/2.png')} alt='play' />
          <Image style={styles.image} width={205} height={444} source={require('@assets/img/store_presence/3.png')} alt='listen' />
          <Image style={styles.image} width={205} height={444} source={require('@assets/img/store_presence/4.png')} alt='earn' />
        </View>
      </WebsiteSection>

      {/* @TODO:
      <WebsiteSection>{/* @TODO: have anchor tag to this for auto scroll /}
        <View style={styles.verseWrapper}>
          <Text style={styles.passageOfTheDayTitle}>{_passage.prettyPassageName()}, {preferredTranslation.abbreviation || ''}</Text>{/* @TODO: includeAbbreviation should probably be kwarg to prettyPassageName to avoid using the preferredTranslation here /}
          <PassageDisplay
            key={_passage.id}
            mainListContainerStyle={styles.passageDisplayOverrides}
            verseTextStyles={styles.passageText}// @TODO: not taking effect...want this to be a pinch smaller
            passages={[_passage]}
            hideLicensing={true}
            showFormatting={false}
            showFootnotes={false}
            showHeadings={false}
            hideCompare={true}
            hideAddToGoal={true}
            isPressDisabled={true}
            textColorOverride={styles.colors.white}
            showWrinkleBadges={false}
          />
          {/* @TODO: share option /}
          {/* @TODO: read more option (take to app, authenticat if necessary) /}
        </View>
      </WebsiteSection>
      */}

      {isPassageDialogOpen &&
        <Dialog
          visible={isPassageDialogOpen}
          onDismiss={() => {
            setPassage({});
            navigation.setParams({}, false); // @TODO: need to worry about other params besides _passage? // @TODO: also, locationState.passage is still {} after this for some reason...
          }}
          fullWidth={true}
        >
          <Dialog.Title>{_passage.prettyPassageName(true)}</Dialog.Title>
          <Dialog.Content style={styles.dialogContent}>
            <View style={styles.passageWrapper}>{/* @TODO: make this a list item instead */}
              <PassageDisplay
                key={_passage.id}
                mainListContainerStyle={styles.passageDisplayOverrides}
                verseTextStyles={styles.passageText}// @TODO: not taking effect...want this to be a pinch smaller
                passages={[_passage]}
                hideLicensing={true}
                showFormatting={false}
                showFootnotes={false}
                showHeadings={false}
                hideCompare={true}
                hideAddToGoal={true}
                isPressDisabled={true}
                textColorOverride={styles.colors.white}
                showWrinkleBadges={false}
                savePassageText={(text) => setPassageText(text)}
              />
            </View>
          </Dialog.Content>
          <Dialog.Actions style={styles.dialogActions}>
            <DownloadLinks />
            <View style={styles.rightActionButtons}>
              <IconButton
                style={styles.actionIconButton}
                mode='contained'
                containerColor={styles.colors.primary}
                icon='book'
                iconColor={styles.colors.white}
                size={styles.globalScale * 20}
                onPress={() => {
                  const chapterId = CPassage.getChapterByBookIdAndNumber(_passage.bookId, +_passage.chapterNumber).id;
                  dispatch(addToReadingHistory(chapterId));
                  navigation.navigate(READ_ROUTE, {
                    chapterId,
                    contextPassage: _passage,
                  });
                }}
              />
              <ShareButton
                style={styles.actionIconButton}
                passage={_passage}
                passageText={_passageText}
                size={styles.globalScale * 20}
                locationForAnalytics='web_landing_page_share_dialog'
              />
              <Button
                mode='contained'
                onPress={() => {
                  setPassage({});
                  navigation.setParams({}, false); // @TODO: need to worry about other params besides passage?
                }}
              >
                {i18n('close')}
              </Button>
            </View>
          </Dialog.Actions>
        </Dialog>
      }

      <WebsiteSection
        isDarkBackground={true}
        title='Purpose'
      >
        <View style={styles.missionAndVisionWrapper}>
          <View style={styles.statsWrapper}>
            <Text style={styles.impactText}><Text style={styles.accentTitle}>Mission:&nbsp;</Text>Increase engagement with God’s Word through memorization.</Text>
          </View>
          <View style={styles.statsWrapper}>
            <Text style={styles.impactText}><Text style={styles.accentTitle}>Vision:&nbsp;</Text>Improve Bible access, literacy, and fluency with intentionality, joy, and excellence.</Text>
          </View>
          <View style={styles.statsWrapper}>
            <Text style={styles.impactText}>
              <Text style={styles.accentTitle}>Impact:&nbsp;</Text>
              {i18n('impactBody',// @TODO: this text is too small (based on gloablScale?...don't apply globalScale if outside of /app)
                <Text style={styles.statText}>{utilities.humanReadableTime(msSpent)}</Text>,
                <Text style={styles.statText}>{utilities.humanReadableNumber(versesReviewed)} {i18n('versesLowercase')}</Text>,
                styles.impactText,
              )}
            </Text>
          </View>
        </View>
      </WebsiteSection>

      <WebsiteSection
        title='Contact us'
      >
        <View style={styles.contactAndDownloadWrapper}>
          <View style={styles.contactWrapper}>
            <ContactForm
              leadingText='Have a question or an idea?  Want to join our mailing list?  Please send us a message!'
              textStyles={styles.paragraph}
            />
          </View>
          <View style={styles.downloadCtaWrapper}>
            <h2>Download now</h2>
            <View style={styles.downloadLinks}>
              <DownloadLinks />
            </View>
          </View>
        </View>
      </WebsiteSection>
    </WebPageStub>
  );
};

export default LandingPage;
